import {
    TimeFrameValues,
    TimeFrameValue,
    TimezoneManager,
} from '@givelify/utils';
import dayjs from 'dayjs';
import { useFilterParams } from './useFilterParams';

const TIME_FRAME_SELECTOR_KEY = 'timeFrameSelector';

const params = ['startDate', 'endDate'] as const;

const getTimeframeFilterFromValues = (values: {
    startDate: string;
    endDate: string;
}): TimeFrameValue => {
    const startValueValid =
        values.startDate && dayjs(values.startDate).isValid();
    const endValueValid = values.endDate && dayjs(values.endDate).isValid();
    if (!startValueValid && !endValueValid) {
        //no dates were provided
        return TimeFrameValues['thisYear'];
    }
    const startStr = startValueValid ? values.startDate : values.endDate;
    const endStr = endValueValid ? values.endDate : values.startDate;

    let startDate = dayjs(startStr, 'YYYY-MM-DD').tz(
        TimezoneManager.getDefault(),
        true,
    );
    if (startDate.isAfter(dayjs().tz().format('YYYY-MM-DD'))) {
        startDate = dayjs().tz();
    }

    let endDate = dayjs(endStr, 'YYYY-MM-DD').tz(
        TimezoneManager.getDefault(),
        true,
    );
    if (endDate.isAfter(dayjs().tz().format('YYYY-MM-DD'))) {
        endDate = dayjs().tz();
    }
    if (startDate.isAfter(endDate)) {
        const temp = startDate;
        startDate = endDate;
        endDate = temp;
    }

    startDate = startDate.startOf('day');
    endDate = endDate.endOf('day');

    const acceptedTimeFrames = [];
    for (const timeFrame of Object.values(TimeFrameValues)) {
        if (
            startDate.isSame(timeFrame.start, 'day') &&
            endDate.isSame(timeFrame.end, 'day')
        ) {
            acceptedTimeFrames.push(timeFrame);
        }
    }

    if (acceptedTimeFrames.length > 1) {
        const selector = localStorage.getItem(TIME_FRAME_SELECTOR_KEY);
        const foundTimeFrame = acceptedTimeFrames.find(
            (timeFrame) => timeFrame.selector === selector,
        );
        if (foundTimeFrame) {
            return foundTimeFrame;
        }
    } else if (acceptedTimeFrames.length === 1) {
        return acceptedTimeFrames[0];
    }

    return {
        selector: 'custom',
        start: startDate,
        end: endDate,
    };
};

export const useTimeframeFilter = (
    timeframe?: TimeFrameValue,
    forceDefault?: boolean,
) => {
    const defaultTimeFrame = TimeFrameValues.thisYear;
    return useFilterParams<TimeFrameValue, typeof params>(
        forceDefault ? timeframe : timeframe ? timeframe : defaultTimeFrame,
        params,
        (state, paramName) => {
            if (state === undefined) return '';
            localStorage.setItem(TIME_FRAME_SELECTOR_KEY, state.selector);
            switch (paramName) {
                case 'startDate':
                    return state.start ? state.start.format('YYYY-MM-DD') : '';
                case 'endDate':
                    return state.end ? state.end.format('YYYY-MM-DD') : '';
            }
        },
        (values) => getTimeframeFilterFromValues(values),
    );
};

export const getTimeframeFilterFromUrlParams = (
    searchParams: URLSearchParams,
) => {
    let timeFrame = TimeFrameValues['thisMonth'];

    const values = {
        startDate: searchParams.get('startDate'),
        endDate: searchParams.get('endDate'),
    };

    const hasSearchParams = values.endDate || values.startDate;
    if (hasSearchParams) {
        timeFrame = getTimeframeFilterFromValues(values);
    }
    return timeFrame;
};

export const getTimeframeFilterFromUrlParamsLifetime = (
    searchParams: URLSearchParams,
) => {
    let timeFrame = TimeFrameValues['lifetime'];

    const values = {
        startDate: searchParams.get('startDate'),
        endDate: searchParams.get('endDate'),
    };

    const hasSearchParams = values.endDate || values.startDate;
    if (hasSearchParams) {
        timeFrame = getTimeframeFilterFromValues(values);
    }
    return timeFrame;
};
